if (!customElements.get('roamory-inline-review')) {
  customElements.define('roamory-inline-review', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.panel = this.closest('.roamory-review-panel');
      this.widget = this.panel?.querySelector('#judgeme_product_reviews');
      this.form = this.querySelector('form');
      this.bodyField = this.querySelector('textarea');
      this.submitButton = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-inline-review-status]');
      try {
        this.labels = JSON.parse(this.querySelector('[data-inline-review-labels]').textContent);
      } catch {
        return;
      }
      if (!this.widget || !this.form) return;
      const signal = this.abort.signal;
      this.form.addEventListener('submit', event => this.continueReview(event), { signal });
      this.form.addEventListener('input', () => {
        this.bodyField.setCustomValidity('');
        this.status.textContent = '';
        this.status.classList.remove('is-error');
      }, { signal });
      this.form.addEventListener('change', () => {
        const score = Number(this.form.querySelector('[name="rating"]:checked')?.value || 0);
        this.querySelectorAll('[data-score]').forEach(star => {
          star.classList.toggle('is-filled', Number(star.dataset.score) <= score);
        });
      }, { signal });
      this.widget.addEventListener('click', event => {
        if (this.openingNative || this.bridgeFailed || this.hidden) return;
        const button = event.target instanceof Element
          ? event.target.closest('[data-testid="write-review-button"]')
          : null;
        if (!button) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.bodyField.focus();
      }, { capture: true, signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.widget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'disabled', 'style']
      });
      this.sync();
    }

    nativeButton() {
      return this.widget?.querySelector('[data-testid="write-review-button"]');
    }

    sync() {
      const button = this.nativeButton();
      if (button !== this.currentButton || !this.allowed) {
        this.panel.classList.remove('has-inline-review');
        this.allowed = Boolean(button && !button.disabled && !button.hidden &&
          button.getClientRects().length && getComputedStyle(button).visibility !== 'hidden');
        this.currentButton = button;
      } else if (!button || button.disabled || button.hidden) {
        this.allowed = false;
      }
      this.hidden = !this.allowed;
      this.panel.classList.toggle('has-inline-review', this.allowed && !this.bridgeFailed);
    }

    waitFor(read, duration = 8000) {
      return new Promise((resolve, reject) => {
        let observer;
        let timer;
        const signal = this.abort.signal;
        const cleanup = () => {
          observer?.disconnect();
          clearTimeout(timer);
          signal.removeEventListener('abort', aborted);
        };
        const aborted = () => {
          cleanup();
          reject(new Error('aborted'));
        };
        const check = () => {
          const result = read();
          if (!result) return;
          cleanup();
          resolve(result);
        };
        observer = new MutationObserver(check);
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
        });
        timer = setTimeout(() => {
          cleanup();
          reject(new Error('timeout'));
        }, duration);
        signal.addEventListener('abort', aborted, { once: true });
        check();
      });
    }

    async continueReview(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      if (!this.bodyField.value.trim()) {
        this.bodyField.setCustomValidity(this.labels.required);
        this.bodyField.reportValidity();
        return;
      }
      const button = this.nativeButton();
      if (!button || button.disabled) {
        this.status.textContent = this.labels.unavailable;
        return;
      }
      this.busy = true;
      this.submitButton.disabled = true;
      this.status.classList.remove('is-error');
      this.status.textContent = this.labels.loading;
      const rating = this.form.querySelector('[name="rating"]:checked').value;
      const body = this.bodyField.value.trim();
      try {
        this.openingNative = true;
        button.click();
        this.openingNative = false;
        const modal = await this.waitFor(() => {
          const element = document.querySelector('.jdgm-write-review-modal');
          return element && getComputedStyle(element).display !== 'none' &&
            element.getAttribute('aria-hidden') !== 'true' ? element : null;
        });
        const repeat = modal.querySelector('.jdgm-write-review-modal__repeat-prompt');
        if (repeat && !repeat.hidden && getComputedStyle(repeat).display !== 'none') {
          this.status.textContent = this.labels.repeat;
          return;
        }
        const normalise = value => value.replace(/\s+/g, ' ').trim();
        const productTitle = modal.querySelector('.jdgm-write-review-modal__product-title');
        if (productTitle && normalise(productTitle.textContent) !== normalise(this.dataset.productTitle)) {
          throw new Error('product-mismatch');
        }
        for (let count = 0; count < 4; count++) {
          const page = modal.querySelector('.jdgm-write-review-modal__page.current-page');
          if (!page) throw new Error('missing-page');
          if (page.dataset.page === '2') break;
          if (page.dataset.page === '1') {
            const star = page.querySelector('.jdgm-star[data-alt="' + rating + '"]');
            if (!star) throw new Error('missing-rating');
            star.click();
          } else if (page.dataset.page === '3' || page.dataset.page === '4') {
            const back = modal.querySelector('.jdgm-write-review-modal__nav-btn-back');
            if (!back) throw new Error('missing-back');
            back.click();
          } else {
            throw new Error('unsupported-page');
          }
          await this.waitFor(() => {
            const current = modal.querySelector('.jdgm-write-review-modal__page.current-page');
            return current && current !== page ? current : null;
          });
          await new Promise(resolve => setTimeout(resolve, 350));
        }
        if (!this.isConnected || this.abort.signal.aborted) return;
        const details = modal.querySelector('.jdgm-write-review-modal__page--review-details.current-page');
        const textarea = details?.querySelector('textarea[name="review_body"]');
        const star = details?.querySelector('.jdgm-star[data-alt="' + rating + '"]');
        if (!textarea || !star) throw new Error('missing-fields');
        star.click();
        textarea.value = body;
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.dispatchEvent(new Event('change', { bubbles: true }));
        const about = modal.querySelector('.jdgm-write-review-modal__page--about-you[data-page="3"]');
        const next = modal.querySelector('.jdgm-write-review-modal__nav-btn-next');
        if (about && about.querySelector('[name="reviewer_email"]') &&
            !details.querySelector('[name="reviewer_email"]') &&
            next && !next.disabled && next.textContent.trim().toLowerCase() === 'next') {
          next.click();
          const page = await this.waitFor(() => {
            const current = modal.querySelector('.jdgm-write-review-modal__page.current-page');
            return current && current !== details ? current : null;
          });
          page.querySelector('input[type="email"]')?.focus({ preventScroll: true });
        } else {
          textarea.focus({ preventScroll: true });
        }
        this.status.textContent = this.labels.ready;
      } catch {
        this.openingNative = false;
        this.bridgeFailed = true;
        this.panel.classList.remove('has-inline-review');
        this.status.classList.add('is-error');
        this.status.textContent = this.labels.error;
      } finally {
        this.busy = false;
        this.submitButton.disabled = false;
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  });
}
